import { round } from "@ugg/shared/utils/math";
import { RoleC, RoleN } from "@ugg/shared/utils/role-helpers";
import { RegionC, RegionN } from "@ugg/shared/utils/region-helpers";
import { RankC, RankN } from "@ugg/shared/utils/rank-helpers";
import { getKey, getData } from "./common";
import { RunesTable, RunesData } from "@ugg/shared/interfaces/champions/runes-table.interface";

export function getRunes(
  data: ReturnType<typeof cleanRunes>,
  params: {
    queueType: string;
    region: string;
    rank?: string;
    role?: string;
  },
) {
  return getData(data, params);
}

export interface RunesTableInfo {
  keystone: Record<
    number,
    {
      wins: number;
      matches: number;
      win_rate: number;
      pick_rate: number;
    }
  >;
  primary: Record<
    number,
    {
      wins: number;
      matches: number;
      win_rate: number;
      pick_rate: number;
    }
  >;
  secondary: Record<
    number,
    {
      wins: number;
      matches: number;
      win_rate: number;
      pick_rate: number;
    }
  >;
}

export function cleanRunes(data: RunesTable) {
  let cleanData: Record<string, RunesTableInfo> = {};

  const regionKeys: RegionN[] = Object.keys(data || {}).map(Number);
  for (const regionId of regionKeys) {
    const regionData = data[regionId];
    const rankKeys: RankN[] = Object.keys(regionData || {}).map(Number);

    for (const rankId of rankKeys) {
      const rankData = regionData[rankId];
      const roleKeys: RoleN[] = Object.keys(rankData || {}).map(Number);

      for (const roleId of roleKeys) {
        const roleData = rankData[roleId];
        let [keystone, primary, secondary] = roleData;

        const matchSum = (runes: Record<number, RunesData>, div = 1) => {
          let totalMatches = 0;
          for (const [runeId, runeData] of Object.entries(runes)) {
            totalMatches += runeData[1];
          }

          return Math.trunc(totalMatches / div);
        };

        const keystoneMatches = matchSum(keystone, 1);
        const primaryMatches = matchSum(primary, 3);
        const secondaryMatches = matchSum(secondary, 2);

        const totalMatches = Math.max(keystoneMatches, primaryMatches, secondaryMatches);

        const [keystoneMap, primaryMap, secondaryMap] = roleData.map((set) => {
          const newData: Record<
            number,
            {
              wins: number;
              matches: number;
              win_rate: number;
              pick_rate: number;
            }
          > = {};
          for (const [runeId, runeData] of Object.entries(set)) {
            const wins = runeData[0];
            const matches = runeData[1];

            newData[parseInt(runeId)] = {
              wins,
              matches,
              win_rate: round((wins / matches) * 100, 2),
              pick_rate: round((matches / totalMatches) * 100, 2),
            };
          }

          return newData;
        });

        const runes = {
          keystone: keystoneMap,
          primary: primaryMap,
          secondary: secondaryMap,
        };

        const key = getKey(RegionC.convertToString(regionId), RankC.convertToString(rankId), RoleC.convertToString(roleId));
        cleanData[key] = runes;
      }
    }
  }

  return cleanData;
}
